import { useState, useRef, useEffect, useCallback } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import styles from "./FaqChat.module.css";
import avatar from "../../images/לילך מורד תדמית 3.webp";

gsap.registerPlugin(ScrollTrigger);

const WHATSAPP_NUMBER = "972505864422";
const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("היי לילך, הגעתי מהאתר שלך ואשמח שנדבר")}`;

function TypingIndicator() {
  return (
    <div className={styles.typingDots}>
      <span className={styles.dot} />
      <span className={styles.dot} />
      <span className={styles.dot} />
    </div>
  );
}

const faqs = [
  {
    q: "מה כולל תהליך תכנון ועיצוב פנים מלא?",
    a: "התהליך מתחיל בפגישת אפיון צרכים מעמיקה שבה אני לומדת את אורח החיים שלכם ומה חשוב לכם בבית. משם אני בונה תוכנית אדריכלית מפורטת עם 2-3 חלופות תכנון, ממשיכה להדמיות ריאליסטיות לאישורכם, מכינה תוכניות עבודה לכל בעל מקצוע ומלווה עם פיקוח עליון בשטח עד לרגע שאתם נכנסים לבית",
  },
  {
    q: "איך מוודאים שהשיפוץ לא חורג מהתקציב?",
    a: "כבר בשלב התכנון אני בונה טבלת תקציב מפורטת שכוללת כל סעיף - מהאינסטלציה ועד הידיות בארונות. לאורך כל הפרויקט אני מעדכנת את הטבלה בזמן אמת וכשצריך לקבל החלטות אני מציגה חלופות שעומדות בתקציב. שקיפות מלאה בכל שלב",
  },
  {
    q: "מה ההבדל בין ליווי עיצובי מלא לפגישת ייעוץ?",
    a: "בליווי מלא אני מנהלת את כל התהליך מקצה לקצה - תכנון אדריכלי, הדמיות, בחירת חומרים וגמרים, תוכניות עבודה מפורטות ופיקוח בשטח. בפגישת ייעוץ אנחנו נפגשים לכמה שעות ממוקדות שבהן אני נותנת כיוון מקצועי, לוח השראה והמלצות מעשיות - ומשם אתם ממשיכים בעצמכם. לא בטוחים מה מתאים לכם? שלחו לי הודעה ואני אגיד לכם בדיוק",
  },
  {
    q: "קיבלנו דירה מקבלן, למה צריך מעצבת פנים?",
    a: "דירת קבלן מגיעה עם מפרט בסיסי שאפשר לשדרג בצורה משמעותית אם יודעים מה לבקש ומתי. אני נכנסת כבר בשלב השינויים מול הקבלן ויודעת בדיוק איפה שווה להשקיע, איפה לחסוך ואיך למנוע טעויות שעולות עשרות אלפי שקלים בהמשך",
  },
  {
    q: "כמה זמן לוקח תהליך עיצוב מלא מההתחלה ועד הכניסה?",
    a: "שלב התכנון והעיצוב לוקח בממוצע בין חודש לחודשיים תלוי במורכבות הפרויקט. שלב הביצוע תלוי בהיקף העבודה ובזמינות של בעלי המקצוע - שיפוץ ממוצע לוקח בין 3-5 חודשים. אני דואגת ללוח זמנים ריאלי ומוודאת שהכל מתקדם לפי התוכנית",
  },
  {
    q: "בן/בת הזוג רוצה סגנון אחר, מה עושים?",
    a: "זה קורה ברוב הפרויקטים וזה בסדר גמור. בפגישת האפיון אני מקשיבה לשני הצדדים ומבינה מה באמת חשוב לכל אחד. בדרך כלל כשמפרקים את זה לרכיבים קטנים - צבעים, חומרים, אווירה - מגלים שיש הרבה יותר הסכמה ממה שחשבתם. ביחד אנחנו מגיעים לעיצוב שמדויק לשניכם",
  },

];

function FaqChat() {
  const [openIndex, setOpenIndex] = useState(null);
  const [typingIndex, setTypingIndex] = useState(null);
  const typingTimeoutRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const el = sectionRef.current;
      if (!el) return;

      gsap.fromTo(
        el.querySelector("[data-faq-header]"),
        { opacity: 0, y: 30 },
        {
          opacity: 1, y: 0, duration: 0.9, ease: "power3.out",
          scrollTrigger: { trigger: el, start: "top 80%", once: true },
        }
      );

      gsap.fromTo(
        el.querySelector("[data-faq-frame]"),
        { opacity: 0, y: 40 },
        {
          opacity: 1, y: 0, duration: 0.8, ease: "power3.out",
          scrollTrigger: { trigger: el, start: "top 70%", once: true },
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  const handleQuestionClick = useCallback(
    (i) => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

      if (openIndex === i) {
        setOpenIndex(null);
        setTypingIndex(null);
        return;
      }

      setOpenIndex(null);
      setTypingIndex(i);

      typingTimeoutRef.current = setTimeout(() => {
        setTypingIndex(null);
        setOpenIndex(i);
      }, 800);
    },
    [openIndex]
  );

  return (
    <section className={styles.faq} ref={sectionRef} id="faq">
      <div className={styles.bgOrb1} />
      <div className={styles.bgOrb2} />

      <div className={styles.container}>
        <div className={styles.header} data-faq-header>
          <h2 className={styles.title}>שאלות נפוצות</h2>
          <p className={styles.subtitle}>
            כל מה שרציתם לדעת לפני שמתחילים תהליך עיצוב
          </p>
          <p className={styles.description}>לחצו על כל שאלה והתשובה תיפתח</p>
        </div>

        <div className={styles.chatFrame} data-faq-frame>
          <div className={styles.messagesArea}>
            {faqs.map((faq, i) => (
              <div key={i} className={styles.messageGroup}>
                <div className={styles.questionRow}>
                  <button
                    className={`${styles.questionBubble} ${openIndex === i ? styles.questionBubbleActive : ""}`}
                    onClick={() => handleQuestionClick(i)}
                  >
                    <h3 className={styles.questionText}>{faq.q}</h3>
                  </button>
                </div>

                {typingIndex === i && (
                  <div className={`${styles.answerRow} ${styles.typingEnter}`}>
                    <img src={avatar} alt="" className={styles.lilachAvatar} loading="lazy" />
                    <div className={styles.answerBubble}>
                      <TypingIndicator />
                    </div>
                  </div>
                )}

                {openIndex === i && (
                  <div className={`${styles.answerRow} ${styles.answerEnter}`}>
                    <img src={avatar} alt="" className={styles.lilachAvatar} loading="lazy" />
                    <div className={styles.answerBubble}>
                      <p>{faq.a}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}

            <div className={styles.ctaMessageGroup}>
              <div className={styles.answerRow}>
                <img src={avatar} alt="" className={styles.lilachAvatar} loading="lazy" />
                <div className={styles.ctaBubble}>
                  <p className={styles.ctaText}>עוד שאלות? אני תמיד כאן</p>
                  <a
                    href={waUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.ctaButton}
                  >
                    <span>דברו איתי</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default FaqChat;
